import api from './api';

class CurrencyService {
  /**
   * Get all available currencies
   */
  async getCurrencies() {
    try {
      const response = await api.get('/common/currencies/');
      return response.data;
    } catch (error) {
      console.error('Error fetching currencies:', error);
      throw error;
    }
  }

  /**
   * Get currency by code
   */
  async getCurrencyByCode(code) {
    try {
      const response = await api.get(`/common/currencies/${code}/`);
      return response.data;
    } catch (error) {
      console.error('Error fetching currency:', error);
      throw error;
    }
  }

  /**
   * Detect currency based on user location
   */
  async detectCurrency() {
    try {
      const response = await api.get('/common/detect-currency/');
      return response.data;
    } catch (error) {
      console.error('Error detecting currency:', error);
      throw error;
    }
  }

  /**
   * Format amount with currency
   */
  formatCurrency(amount, currency) {
    // Convert amount to number
    const numAmount = parseFloat(amount) || 0;

    if (!currency) {
      return numAmount.toString();
    }

    const formatter = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency.code,
      minimumFractionDigits: currency.decimal_places || 2,
      maximumFractionDigits: currency.decimal_places || 2,
    });

    try {
      return formatter.format(numAmount);
    } catch (error) {
      // Fallback if currency code is not supported by Intl.NumberFormat
      return `${currency.symbol || currency.code} ${numAmount.toFixed(currency.decimal_places || 2)}`;
    }
  }

  /**
   * Format amount with currency symbol only
   */
  formatCurrencySymbol(amount, currency) {
    // Convert amount to number
    const numAmount = parseFloat(amount) || 0;

    if (!currency) {
      return numAmount.toString();
    }

    const symbol = currency.symbol || currency.code;
    const decimals = currency.decimal_places || 2;
    return `${symbol}${numAmount.toFixed(decimals)}`;
  }

  /**
   * Get currency symbol
   */
  getCurrencySymbol(currency) {
    if (!currency) {
      return '';
    }
    return currency.symbol || currency.code;
  }

  /**
   * Convert amount to display format
   */
  toDisplayAmount(amount, currency) {
    if (!currency) {
      return amount;
    }

    const decimals = currency.decimal_places || 2;
    return parseFloat(amount).toFixed(decimals);
  }

  /**
   * Parse display amount to decimal
   */
  parseDisplayAmount(displayAmount) {
    return parseFloat(displayAmount) || 0;
  }

  /**
   * Validate currency amount
   */
  validateAmount(amount, currency) {
    const numAmount = parseFloat(amount);

    if (isNaN(numAmount) || numAmount < 0) {
      return false;
    }

    if (currency && currency.decimal_places === 0) {
      return Number.isInteger(numAmount);
    }

    return true;
  }

  /**
   * Get popular currencies for selection
   */
  getPopularCurrencies() {
    return [
      { code: 'USD', name: 'US Dollar', symbol: '$' },
      { code: 'EUR', name: 'Euro', symbol: '€' },
      { code: 'GBP', name: 'British Pound', symbol: '£' },
      { code: 'JPY', name: 'Japanese Yen', symbol: '¥' },
      { code: 'CAD', name: 'Canadian Dollar', symbol: 'C$' },
      { code: 'AUD', name: 'Australian Dollar', symbol: 'A$' },
      { code: 'CHF', name: 'Swiss Franc', symbol: 'CHF' },
      { code: 'CNY', name: 'Chinese Yuan', symbol: '¥' },
      { code: 'INR', name: 'Indian Rupee', symbol: '₹' },
      { code: 'PHP', name: 'Philippine Peso', symbol: '₱' },
    ];
  }
}

const currencyServiceInstance = new CurrencyService();
export default currencyServiceInstance;
